import { useMediaQuery } from '@uhg-abyss/web/hooks/useMediaQuery';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Tooltip } from '@uhg-abyss/web/ui/Tooltip';
import { useShallow } from 'zustand/react/shallow';

import { Address as ProviderAddress } from '../../models/ProviderDetails';
import { useSearchStore } from '../../store/useSearchStore';
import { SearchState } from '../../store/useSearchStore/searchStore';
import { shortenZipCode } from '../../utils/providerDetails.utils';
import { tabletScreen } from '../ConstantsStyles';
import {
  AddressText,
  AddressWrapper,
  Dot,
  GroupedPinAddressText,
  additionalAddressTextStyles,
  addressListViewTextToolTipStyles,
  addressTextToolTipStyles,
  popupAddressTextToolTipStyles,
  relocatedCompareAddressMaxWidthStyles,
} from './DataCard.style';

type Props = {
  address?: ProviderAddress;
  expandedProviderListView?: boolean;
  id?: string;
  popupContainer?: boolean;
  sectionType?: string;
  isCTADisplayLogic?: boolean;
  showSmartChoice?: boolean;
  badgeCardType?: string;
  enableMapViewHideOption?: boolean;
  isGroupedLocationDrawer?: boolean;
  enableCompareButtonRelocation?: boolean;
};
const maxWidthWithnewDesign = (
  expandedProviderListView: boolean,
  enableMapViewHideOption: boolean,
  enableCompareButtonRelocation: boolean,
  compareToggleOn: boolean
) => {
  if (expandedProviderListView || !enableMapViewHideOption) {
    return {};
  }

  if (enableCompareButtonRelocation && compareToggleOn) {
    return relocatedCompareAddressMaxWidthStyles();
  }

  return {
    maxWidth: '180px !important',
    '@media (min-width: 360px) and (max-width: 400px)': {
      maxWidth: '130px !important',
    },
  };
};
const maxWidthAddress = (
  showSmartChoice: boolean,
  maxWidthWithBadgeType: Record<string, unknown>,
  expandedProviderListView: boolean,
  enableMapViewHideOption: boolean,
  enableCompareButtonRelocation: boolean,
  compareToggleOn: boolean
) =>
  showSmartChoice
    ? {
        '&.abyss-text-root': { maxWidth: '180px' },
        ...maxWidthWithBadgeType,
        ...maxWidthWithnewDesign(
          expandedProviderListView,
          enableMapViewHideOption,
          enableCompareButtonRelocation,
          compareToggleOn
        ),
      }
    : {};
const tooltipStyles = (
  enableMapViewHideOption,
  expandedProviderListView,
  enableCompareButtonRelocation,
  compareToggleOn
) =>
  enableMapViewHideOption
    ? addressListViewTextToolTipStyles(
        expandedProviderListView,
        enableCompareButtonRelocation,
        compareToggleOn
      )
    : addressTextToolTipStyles;

export const Address = ({
  address,
  expandedProviderListView = false,
  enableMapViewHideOption = false,
  id,
  popupContainer,
  sectionType,
  isCTADisplayLogic = false,
  showSmartChoice = false,
  badgeCardType = 'data-card',
  isGroupedLocationDrawer = false,
  enableCompareButtonRelocation = false,
}: Props) => {
  const TabletScreen = useMediaQuery(tabletScreen);
  const { compareToggleOn } = useSearchStore(
    useShallow((state: SearchState) => ({
      compareToggleOn: state.searchStore.compareToggleOn,
    }))
  );
  const { line } = address || {};
  const addressLine = line?.join(', ');
  const addressFullText = `${addressLine} ${address?.city ?? ''}, ${
    address?.state ?? ''
  } ${shortenZipCode(address?.postalCode) ?? ''}`;
  const maxWidthWithBadgeType =
    badgeCardType === 'data-card' ? { maxWidth: '220px !important' } : {};

  if (!line) return null;

  const renderFullTextAddress = () => {
    const shortenZip = shortenZipCode(address?.postalCode);
    let fullText =
      address && address.city && address.state
        ? `${address.city}, ${address.state}`
        : '';

    if (fullText.length > 60) {
      fullText = `${fullText.slice(0, 60)}...`;
    }

    return (
      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <span style={{ whiteSpace: 'nowrap' }}>{addressLine}</span>
        <br />
        <span style={{ whiteSpace: 'nowrap' }}>
          {fullText} {shortenZip}
        </span>
      </div>
    );
  };

  return (
    <AddressWrapper>
      {!isCTADisplayLogic && !popupContainer && !isGroupedLocationDrawer && (
        <Dot />
      )}
      {isGroupedLocationDrawer ? (
        <GroupedPinAddressText
          color={'$core.color.neutral.90'}
          data-auto-testid={`data-card-address-${sectionType}-${id}`}
          data-testid={`data-card-address-${sectionType}-${id}`}
        >
          <Flex>{`${addressLine}`}</Flex>
          <Flex>
            {`${address?.city ?? ''}, ${address?.state ?? ''} ${
              shortenZipCode(address?.postalCode) ?? ''
            }`}
          </Flex>
        </GroupedPinAddressText>
      ) : (
        <Tooltip
          content={enableMapViewHideOption ? addressFullText : addressLine}
          css={
            popupContainer
              ? popupAddressTextToolTipStyles
              : tooltipStyles(
                  enableMapViewHideOption,
                  expandedProviderListView,
                  enableCompareButtonRelocation,
                  compareToggleOn
                )
          }
          position="top"
        >
          <span
            style={{
              display: 'block',
              maxWidth: popupContainer ? '150px' : '',
              overflow: 'hidden',
            }}
          >
            <AddressText
              color={
                popupContainer
                  ? '$core.color.neutral.80'
                  : isCTADisplayLogic
                  ? '$core.color.brand.60'
                  : '$core.color.neutral.80'
              }
              css={{
                marginTop: '0px !important',
                fontSize: popupContainer && '$sm',
                fontWeight: expandedProviderListView
                  ? '100 !important'
                  : '100 !important',
                lineHeight: expandedProviderListView
                  ? '20px !important'
                  : '15px !important',
                padding: popupContainer && '0px 10px',
                height: enableMapViewHideOption && '50px',
                ...additionalAddressTextStyles(
                  expandedProviderListView,
                  enableMapViewHideOption,
                  TabletScreen,
                  popupContainer
                ),
                '@media (min-width: 360px) and (max-width: 400px)': {
                  maxWidth: '130px !important',
                },
                ...maxWidthAddress(
                  showSmartChoice,
                  maxWidthWithBadgeType,
                  expandedProviderListView,
                  enableMapViewHideOption,
                  enableCompareButtonRelocation,
                  compareToggleOn || false
                ),
              }}
              cssProps={{
                enableMapViewHideOption,
              }}
              data-auto-testid={`data-card-address-${sectionType}-${id}`}
              data-testid={`data-card-address-${sectionType}-${id}`}
              fontWeight={
                popupContainer
                  ? 'regular'
                  : isCTADisplayLogic
                  ? 'bold'
                  : 'regular'
              }
            >
              {enableMapViewHideOption ? renderFullTextAddress() : addressLine}
            </AddressText>
          </span>
        </Tooltip>
      )}
    </AddressWrapper>
  );
};
